import './PromoAppliance.scss';

import React from 'react';
import { useTranslation } from 'react-i18next';

import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import { useProductReviews } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useProductReviews';
import {
    useProductInventory,
    useProductPrice,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store/queries/useProductsPriceAndInventories';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { Rating } from '@sbi-international/boutik.gatsby.plugin.components';
import { popFrontURL } from '../../utils';
import { IAppliance } from '../../interfaces';
import GatsbyImageWrapper from '../GatsbyImageWrapper';
import { DroletButton } from '../Primitives';

export default function PromoApplianceSingle(props: {
    appliance: IAppliance;
    id?: string;
}) {
    const { appliance, id } = props;
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const [price] = useProductPrice(appliance.code as string);
    const { t } = useTranslation('pages');
    const promoRange = useProductInventory(appliance?.code as string);
    const fromDate =
        promoRange?.[0]?.stock?.specialPriceFromDate?.split(' ')?.[0];
    const toDate = promoRange?.[0]?.stock?.specialPriceEndDate?.split(' ')?.[0];
    const dates = getDateStr(fromDate, toDate, t);

    const productReviews = useProductReviews(appliance.code);

    const goToSinglePage = () => {
        navigateTranslated(popFrontURL(appliance?.slug?.[currentLocale], 2));
    };
    return (
        <div className='appliance-single-promo' id={id || undefined}>
            {true && (
                <div className='discount-tag'>{getPricePercentage(price)}</div>
            )}
            <div className='single-image' onClick={() => goToSinglePage()}>
                <GatsbyImageWrapper fields={appliance.fields} />
            </div>
            <div className='single-content'>
                <span className='single-size'>
                    {appliance?.attributes?.size?.values?.[0]?.[currentLocale]}
                </span>
                <span className='single-title'>
                    {appliance?.title?.[currentLocale]}
                </span>
                <span className='line'></span>
                <span className='single-sku'>{appliance?.code}</span>
                <span className='single-price'>{getProductPrice()}</span>
                {productReviews && productReviews?.count > 0 && (
                    <div className='single-ratings'>
                        <div className='rating'>
                            <Rating.RatingScore
                                ratingScore={productReviews?.average ?? 0}
                                ratingCount={productReviews?.count}
                            />
                        </div>
                    </div>
                )}
                {dates && <div className='date-range'>{dates}</div>}
                <DroletButton
                    label={t('home.see-product')}
                    onClick={() => goToSinglePage()}
                />
            </div>
        </div>
    );
    function getProductPrice() {
        if (price?.regularPrice.value === price?.finalPrice.value) {
            return (
                <span className='normal-price'>
                    {LocalePrice(currentLocale, price?.regularPrice.value)}
                </span>
            );
        } else if (
            price?.regularPrice.value &&
            price?.finalPrice.value &&
            price.regularPrice.value !== price.finalPrice.value
        ) {
            return (
                <div className='discount-price'>
                    <span className='regular-price'>
                        {LocalePrice(currentLocale, price?.regularPrice.value)}
                    </span>
                    <span className='final-price'>
                        {LocalePrice(currentLocale, price?.finalPrice.value)}
                    </span>
                </div>
            );
        }
    }
}

function getPricePercentage(price: any): string {
    if (price?.discount?.percentOff)
        return `-${price?.discount?.percentOff?.toFixed(0)}%`;
    else {
        const result =
            (100 * (price?.regularPrice?.value - price?.finalPrice?.value)) /
            price?.regularPrice?.value;
        const str =
            price?.regularPrice?.value === 0 || price?.finalPrice?.value === 0
                ? `--`
                : `-${result.toFixed(0)}%`;
        return str;
    }
}

function getDateStr(
    fromDate: string | undefined,
    toDate: string | undefined,
    t: any
): string | undefined {
    if ((!fromDate && !toDate) || (fromDate && !toDate)) return undefined;
    else if (!fromDate && toDate)
        return `${t('layout:appliance.valid-until')} ${toDate}`;
    else return `${t('promotions.date-label', { from: fromDate, to: toDate })}`;
}
